import { trackEvent } from 'shared/helpers/Analytics';
import { ReportsFiltersValue } from './ReportsDTO';

export type ReportsDashboard =
  | 'overview'
  | 'revenue'
  | 'expenses'
  | 'delivery_trend'
  | 'invoices';

export type ReportsFiltersSource = 'filter_bar' | 'filters_drawer';

export type ReportsUpgradeTrigger =
  | 'sidebar_menu'
  | 'period_field'
  | 'restricted_page';

const REPORTS_DASHBOARD_PATHS: Array<[string, ReportsDashboard]> = [
  ['/reports/overview', 'overview'],
  ['/reports/revenue', 'revenue'],
  ['/reports/expenses', 'expenses'],
  ['/reports/delivery-trend', 'delivery_trend'],
  ['/reports/invoices', 'invoices'],
];

type ReportsAnalyticsEvent =
  | {
      name:
        | 'Carrier Opened Reporting Tab'
        | 'Carrier Viewed Reporting Dashboard';
      dashboard: ReportsDashboard;
      is_paying: boolean;
      is_reporting_locked: boolean;
    }
  | {
      name: 'Carrier Changed Reporting Period';
      dashboard: ReportsDashboard;
      start_date: string | null;
      end_date: string | null;
    }
  | {
      name: 'Carrier Filtered Reporting Dashboard';
      dashboard: ReportsDashboard;
      source: ReportsFiltersSource;
      filters: ReportsFiltersValue;
      filter?: string;
    }
  | {
      name: 'Carrier Cleared Reporting Dashboard Filters';
      dashboard: ReportsDashboard;
      source: ReportsFiltersSource;
    }
  | {
      name: 'Carrier Clicked Send Report' | 'Carrier Clicked Download Report';
      dashboard: ReportsDashboard;
    }
  | {
      name: 'Carrier Sent Report';
      dashboard: ReportsDashboard;
      recipients_count: number;
      is_filters_applied: boolean;
      filters: ReportsFiltersValue;
    }
  | {
      name: 'Carrier Opened Reporting Upgrade Dialog';
      dashboard: ReportsDashboard | null;
      trigger: ReportsUpgradeTrigger;
    };

export function resolveReportsDashboard(
  pathname: string,
): ReportsDashboard | null {
  const match = REPORTS_DASHBOARD_PATHS.find(([path]) =>
    pathname.startsWith(path),
  );

  return match ? match[1] : null;
}

function toCSV(values: string[]): string | undefined {
  return !values.length
    ? undefined
    : [...values].sort((a, b) => a.localeCompare(b)).join(', ');
}

function formatFilters(filters: ReportsFiltersValue): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  const usedFilters: string[] = [];

  Object.entries(filters).forEach(([filter, values]) => {
    const value = toCSV(values);

    if (value != null) {
      props[`filter_${filter}`] = value;
      usedFilters.push(filter);
    }
  });

  props.used_filters = toCSV(usedFilters);
  props.used_filters_count = usedFilters.length;

  return props;
}

function extractProps(event: ReportsAnalyticsEvent): Record<string, unknown> {
  if ('filters' in event) {
    const { name, filters, ...props } = event;

    return { ...props, ...formatFilters(filters) };
  }

  const { name, ...props } = event;

  return { ...props };
}

export function trackReportsEvent(event: ReportsAnalyticsEvent) {
  trackEvent(event.name, extractProps(event));
}
